import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import * as api from '../services/api';
import DashboardCard from '../components/DashboardCard';

const Container = styled.div`
  @media (max-width: 768px) {
    padding: 16px;
  }
  padding: 24px;
  max-width: 1200px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin: 0 0 24px 0;
  font-size: 14px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 24px 0 12px 0;
`;

const SectionDescription = styled.p`
  color: #6b7280;
  margin: 0 0 12px 0;
  font-size: 13px;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 8px;
`;

const StatCard = styled.div<{ $loading?: boolean }>`
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  opacity: ${(p) => (p.$loading ? 0.7 : 1)};
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 4px;
  line-height: 1.3;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
`;

const CorrelationSection = styled.section`
  margin-top: 32px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 20px;
  overflow: auto;
`;

const CorrelationHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`;

const CorrelationSectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #374151;
  margin: 0;
`;

const RegenerateButton = styled.button<{ $loading?: boolean }>`
  padding: 8px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: ${(p) => (p.$loading ? 'wait' : 'pointer')};
  opacity: ${(p) => (p.$loading ? 0.8 : 1)};
  &:hover:not(:disabled) {
    background: #e5e7eb;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const CorrelationGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
  gap: 20px;
`;

const CorrelationImageWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  min-height: 180px;
  background: #fff;
  border-radius: 6px;
  padding: 12px;
`;

const CorrelationImage = styled.img`
  max-width: 100%;
  height: auto;
  display: block;
`;

const ErrorText = styled.p`
  color: #dc2626;
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const LoadingText = styled.p`
  color: #6b7280;
  font-size: 14px;
  margin: 8px 0 0 0;
`;

const InsightSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
`;
const InsightLabel = styled.label`
  display: block;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  margin-bottom: 6px;
`;
const InsightTextarea = styled.textarea`
  width: 100%;
  min-height: 72px;
  padding: 8px 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  resize: vertical;
  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
  }
`;
const InsightSaveButton = styled.button<{ $saving?: boolean }>`
  margin-top: 8px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background: #2563eb;
  border: none;
  border-radius: 6px;
  cursor: ${(p) => (p.$saving ? 'wait' : 'pointer')};
  opacity: ${(p) => (p.$saving ? 0.8 : 1)};
  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
  &:disabled {
    cursor: not-allowed;
  }
`;

const ConceptualInsightLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin: 10px 0 4px 0;
`;
const ConceptualInsightBody = styled.p`
  font-size: 13px;
  color: #374151;
  line-height: 1.55;
  margin: 0;
`;

const CARD_IDS = ['neckcare-chyps', 'chyps-marcons', 'vng-latency-butterfly'] as const;

const CONCEPTUAL_INSIGHT_BY_CARD: Record<(typeof CARD_IDS)[number], string> = {
  'neckcare-chyps':
    'Higher visual motion sensitivity may be associated with worse cervical proprioception control. This inverse relationship is directionally consistent with altered cervical-visual-vestibular integration, in which reduced cervical sensorimotor regulation may accompany greater sensitivity to visually dynamic environments.',
  'chyps-marcons':
    'Visual hypersensitivity can reflect increased sensory gain or reduced sensory filtering in visual processing networks. If broader regulatory systems are dysregulated (e.g., autonomic, neuroimmune, or central sensory processing), that same physiological context might also be associated with persistent microbial colonization patterns like MARCoNS.',
  'vng-latency-butterfly':
    'If VNG latency is increasing while cervical proprioception control is worsening, that is physiologically plausible and directionally coherent with impaired cervical-visual-vestibular sensorimotor integration, but it is not specific enough to conclude causation.',
};

const Dashboard: React.FC = () => {
  const [patientCounts, setPatientCounts] = useState<api.ModalityStat[]>([]);
  const [measurementCounts, setMeasurementCounts] = useState<api.ModalityStat[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [correlationImageUrl, setCorrelationImageUrl] = useState<string | null>(null);
  const [correlationLoading, setCorrelationLoading] = useState(true);
  const [correlationError, setCorrelationError] = useState<string | null>(null);
  const [chypsMarconsImageUrl, setChypsMarconsImageUrl] = useState<string | null>(null);
  const [chypsMarconsLoading, setChypsMarconsLoading] = useState(true);
  const [chypsMarconsError, setChypsMarconsError] = useState<string | null>(null);
  const [vngLatencyButterflyImageUrl, setVngLatencyButterflyImageUrl] = useState<string | null>(null);
  const [vngLatencyButterflyLoading, setVngLatencyButterflyLoading] = useState(true);
  const [vngLatencyButterflyError, setVngLatencyButterflyError] = useState<string | null>(null);
  /** Refresh key per card: bump to refetch only that card's image. */
  const [neckcareChypsRefreshKey, setNeckcareChypsRefreshKey] = useState<number | null>(null);
  const [chypsMarconsRefreshKey, setChypsMarconsRefreshKey] = useState<number | null>(null);
  const [vngLatencyButterflyRefreshKey, setVngLatencyButterflyRefreshKey] = useState<number | null>(null);
  const [regenerateAllLoading, setRegenerateAllLoading] = useState(false);
  const [regenerateNeckcareLoading, setRegenerateNeckcareLoading] = useState(false);
  const [regenerateChypsMarconsLoading, setRegenerateChypsMarconsLoading] = useState(false);
  const [regenerateVngLatencyButterflyLoading, setRegenerateVngLatencyButterflyLoading] = useState(false);
  const [insights, setInsights] = useState<Record<string, string>>({});
  const [insightSaving, setInsightSaving] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let cancelled = false;
    api
      .getDashboardModalityStats()
      .then((res) => {
        if (!cancelled && res.success && res.data) {
          setPatientCounts(res.data.patient_counts ?? []);
          setMeasurementCounts(res.data.measurement_counts ?? []);
        }
      })
      .catch((e) => {
        if (!cancelled) setStatsError(e.message || 'Failed to load modality stats');
      })
      .finally(() => {
        if (!cancelled) setStatsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setCorrelationLoading(true);
    setCorrelationError(null);
    const cacheBuster = neckcareChypsRefreshKey != null ? String(neckcareChypsRefreshKey) : undefined;
    api
      .getDashboardCorrelationNeckcareChypsBlob(cacheBuster)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setCorrelationImageUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setCorrelationError(e.message || 'Failed to load correlation plot');
      })
      .finally(() => {
        if (!cancelled) setCorrelationLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [neckcareChypsRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setChypsMarconsLoading(true);
    setChypsMarconsError(null);
    const cacheBuster = chypsMarconsRefreshKey != null ? String(chypsMarconsRefreshKey) : undefined;
    api
      .getDashboardCorrelationChypsMarconsBlob(cacheBuster)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setChypsMarconsImageUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setChypsMarconsError(e.message || 'Failed to load Visual Motion Sensitivity–MARCoNS (nasal bacterial culture test) plot');
      })
      .finally(() => {
        if (!cancelled) setChypsMarconsLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [chypsMarconsRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setVngLatencyButterflyLoading(true);
    setVngLatencyButterflyError(null);
    const cacheBuster = vngLatencyButterflyRefreshKey != null ? String(vngLatencyButterflyRefreshKey) : undefined;
    api
      .getDashboardCorrelationVngLatencyButterflyBlob(cacheBuster)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setVngLatencyButterflyImageUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) setVngLatencyButterflyError(e.message || 'Failed to load VNG Latency vs Cervical Proprioception Control plot');
      })
      .finally(() => {
        if (!cancelled) setVngLatencyButterflyLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [vngLatencyButterflyRefreshKey]);

  useEffect(() => {
    let cancelled = false;
    CARD_IDS.forEach((cardId) => {
      api.getDashboardInsight(cardId).then((res) => {
        if (!cancelled && res.success && res.data) setInsights((prev) => ({ ...prev, [cardId]: res.data!.body ?? '' }));
      }).catch(() => {});
    });
    return () => { cancelled = true; };
  }, []);

  const handleInsightChange = (cardId: string, value: string) => {
    setInsights((prev) => ({ ...prev, [cardId]: value }));
  };
  const handleInsightSave = (cardId: string) => {
    setInsightSaving((prev) => ({ ...prev, [cardId]: true }));
    api.putDashboardInsight(cardId, insights[cardId] ?? '').then((res) => {
      if (res.success && res.data) setInsights((prev) => ({ ...prev, [cardId]: res.data!.body ?? '' }));
    }).finally(() => setInsightSaving((prev) => ({ ...prev, [cardId]: false })));
  };

  const handleRegenerateAll = () => {
    setRegenerateAllLoading(true);
    api
      .getDashboardCorrelationRegenerate('all')
      .then((res) => {
        if (res.success) {
          const t = Date.now();
          setNeckcareChypsRefreshKey(t);
          setChypsMarconsRefreshKey(t);
          setVngLatencyButterflyRefreshKey(t);
        } else {
          setCorrelationError(res.message || 'Regenerate failed');
        }
      })
      .catch((e) => {
        setCorrelationError(e.message || 'Regenerate failed');
      })
      .finally(() => {
        setRegenerateAllLoading(false);
      });
  };

  const handleRegenerateNeckcareChyps = () => {
    setRegenerateNeckcareLoading(true);
    setCorrelationError(null);
    api
      .getDashboardCorrelationRegenerate('neckcare-chyps')
      .then((res) => {
        if (res.success) setNeckcareChypsRefreshKey(Date.now());
        else setCorrelationError(res.message || 'Regenerate failed');
      })
      .catch((e) => setCorrelationError(e.message || 'Regenerate failed'))
      .finally(() => setRegenerateNeckcareLoading(false));
  };

  const handleRegenerateChypsMarcons = () => {
    setRegenerateChypsMarconsLoading(true);
    setChypsMarconsError(null);
    api
      .getDashboardCorrelationRegenerate('chyps-marcons')
      .then((res) => {
        if (res.success) setChypsMarconsRefreshKey(Date.now());
        else setChypsMarconsError(res.message || 'Regenerate failed');
      })
      .catch((e) => setChypsMarconsError(e.message || 'Regenerate failed'))
      .finally(() => setRegenerateChypsMarconsLoading(false));
  };

  const handleRegenerateVngLatencyButterfly = () => {
    setRegenerateVngLatencyButterflyLoading(true);
    setVngLatencyButterflyError(null);
    api
      .getDashboardCorrelationRegenerate('vng-latency-butterfly')
      .then((res) => {
        if (res.success) setVngLatencyButterflyRefreshKey(Date.now());
        else setVngLatencyButterflyError(res.message || 'Regenerate failed');
      })
      .catch((e) => setVngLatencyButterflyError(e.message || 'Regenerate failed'))
      .finally(() => setRegenerateVngLatencyButterflyLoading(false));
  };

  return (
    <Container>
      <Title>Dashboard</Title>
      <Subtitle>
        Clinical data should not just be archived, it should be utilized. Our platform turns everyday clinical data into actionable insights, bringing AI-powered, data-driven decision support to clinicians at the point of care.
      </Subtitle>

      <SectionTitle>Patient Counts (N)</SectionTitle>
      <SectionDescription>Number of patients per modality, device, or data type (big N).</SectionDescription>
      {statsError && <ErrorText>{statsError}</ErrorText>}
      <StatsGrid>
        {patientCounts.map((m) => (
          <StatCard key={m.key} $loading={statsLoading}>
            <StatLabel>{m.label}</StatLabel>
            <StatValue>{statsLoading ? '…' : m.count}</StatValue>
          </StatCard>
        ))}
      </StatsGrid>

      <SectionTitle>Diagnostic Tests</SectionTitle>
      <SectionDescription>Counts of tests or measurements (e.g. Number Of Saccades, Cervical Proprioception Control, Range Of Motion (ROM), Joint Position Error (JPE), MARCoNS Test Positivity).</SectionDescription>
      <StatsGrid>
        {measurementCounts.map((m) => (
          <StatCard key={m.key} $loading={statsLoading}>
            <StatLabel>{m.label}</StatLabel>
            <StatValue>{statsLoading ? '…' : m.count}</StatValue>
          </StatCard>
        ))}
      </StatsGrid>

      {!statsLoading && patientCounts.some((m) => m.count > 0) && (
      <CorrelationSection>
        <CorrelationHeader>
          <CorrelationSectionTitle>Clinical Data Insights</CorrelationSectionTitle>
          <RegenerateButton
            type="button"
            onClick={handleRegenerateAll}
            disabled={regenerateAllLoading}
            $loading={regenerateAllLoading}
          >
            {regenerateAllLoading ? 'Regenerating…' : 'Regenerate all'}
          </RegenerateButton>
        </CorrelationHeader>
        <CorrelationGrid>
          <DashboardCard
            title="Visual Motion Sensitivity vs Cervical Proprioception Control"
            description="Visual Motion Sensitivity (Symptoms Profile scale) vs Cervical Proprioception Control. Blurred background, orange = post-therapy; dashed line = fit."
            regenerate={{
              onClick: handleRegenerateNeckcareChyps,
              loading: regenerateNeckcareLoading,
              label: 'Regenerate',
            }}
          >
            <CorrelationImageWrapper>
              {correlationLoading && <LoadingText>Loading…</LoadingText>}
              {correlationError && <ErrorText>{correlationError}</ErrorText>}
              {correlationImageUrl && !correlationError && (
                <CorrelationImage src={correlationImageUrl} alt="Visual Motion Sensitivity vs Cervical Proprioception Control" />
              )}
            </CorrelationImageWrapper>
            <ConceptualInsightLabel>Conceptual insights</ConceptualInsightLabel>
            <ConceptualInsightBody>{CONCEPTUAL_INSIGHT_BY_CARD['neckcare-chyps']}</ConceptualInsightBody>
            <InsightSection>
              <InsightLabel htmlFor="insight-neckcare-chyps">Clinical notes</InsightLabel>
              <InsightTextarea
                id="insight-neckcare-chyps"
                value={insights['neckcare-chyps'] ?? ''}
                onChange={(e) => handleInsightChange('neckcare-chyps', e.target.value)}
                placeholder="Type your clinical insights or notes…"
              />
              <InsightSaveButton type="button" $saving={insightSaving['neckcare-chyps']} onClick={() => handleInsightSave('neckcare-chyps')} disabled={insightSaving['neckcare-chyps']}>
                {insightSaving['neckcare-chyps'] ? 'Saving…' : 'Save'}
              </InsightSaveButton>
            </InsightSection>
          </DashboardCard>
          <DashboardCard
            title="Visual Motion Sensitivity vs MARCoNS (nasal bacterial culture test) test positivity"
            description="Diagnosis grid: Visual Motion Sensitivity score binned at 25 (&lt; 25 vs ≥ 25), four quadrants (green = MARCoNS Negative, orange = Positive). One point per patient; Pearson r shown."
            regenerate={{
              onClick: handleRegenerateChypsMarcons,
              loading: regenerateChypsMarconsLoading,
              label: 'Regenerate',
            }}
          >
            <CorrelationImageWrapper>
              {chypsMarconsLoading && <LoadingText>Loading…</LoadingText>}
              {chypsMarconsError && <ErrorText>{chypsMarconsError}</ErrorText>}
              {chypsMarconsImageUrl && !chypsMarconsError && (
                <CorrelationImage src={chypsMarconsImageUrl} alt="Visual Motion Sensitivity vs MARCoNS (nasal bacterial culture test)" />
              )}
            </CorrelationImageWrapper>
            <ConceptualInsightLabel>Conceptual insights</ConceptualInsightLabel>
            <ConceptualInsightBody>{CONCEPTUAL_INSIGHT_BY_CARD['chyps-marcons']}</ConceptualInsightBody>
            <InsightSection>
              <InsightLabel htmlFor="insight-chyps-marcons">Clinical notes</InsightLabel>
              <InsightTextarea
                id="insight-chyps-marcons"
                value={insights['chyps-marcons'] ?? ''}
                onChange={(e) => handleInsightChange('chyps-marcons', e.target.value)}
                placeholder="Type your clinical insights or notes…"
              />
              <InsightSaveButton type="button" $saving={insightSaving['chyps-marcons']} onClick={() => handleInsightSave('chyps-marcons')} disabled={insightSaving['chyps-marcons']}>
                {insightSaving['chyps-marcons'] ? 'Saving…' : 'Save'}
              </InsightSaveButton>
            </InsightSection>
          </DashboardCard>
          <DashboardCard
            title="VNG Latency vs Cervical Proprioception Control"
            description="VNG (e.g. VisualEyes) saccade latency [ms] vs NeckCare Butterfly Test score (Cervical Proprioception Control). One point per matched patient; Pearson r and n shown."
            regenerate={{
              onClick: handleRegenerateVngLatencyButterfly,
              loading: regenerateVngLatencyButterflyLoading,
              label: 'Regenerate',
            }}
          >
            <CorrelationImageWrapper>
              {vngLatencyButterflyLoading && <LoadingText>Loading…</LoadingText>}
              {vngLatencyButterflyError && <ErrorText>{vngLatencyButterflyError}</ErrorText>}
              {vngLatencyButterflyImageUrl && !vngLatencyButterflyError && (
                <CorrelationImage src={vngLatencyButterflyImageUrl} alt="VNG Latency vs Cervical Proprioception Control" />
              )}
            </CorrelationImageWrapper>
            <ConceptualInsightLabel>Conceptual insights</ConceptualInsightLabel>
            <ConceptualInsightBody>{CONCEPTUAL_INSIGHT_BY_CARD['vng-latency-butterfly']}</ConceptualInsightBody>
            <InsightSection>
              <InsightLabel htmlFor="insight-vng-latency-butterfly">Clinical notes</InsightLabel>
              <InsightTextarea
                id="insight-vng-latency-butterfly"
                value={insights['vng-latency-butterfly'] ?? ''}
                onChange={(e) => handleInsightChange('vng-latency-butterfly', e.target.value)}
                placeholder="Type your clinical insights or notes…"
              />
              <InsightSaveButton type="button" $saving={insightSaving['vng-latency-butterfly']} onClick={() => handleInsightSave('vng-latency-butterfly')} disabled={insightSaving['vng-latency-butterfly']}>
                {insightSaving['vng-latency-butterfly'] ? 'Saving…' : 'Save'}
              </InsightSaveButton>
            </InsightSection>
          </DashboardCard>
        </CorrelationGrid>
      </CorrelationSection>
      )}
    </Container>
  );
};

export default Dashboard;
